import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import getOpcoesConsulta from "../../service/getOpcoesConsulta";

const isTipoArquivoPermitido = (tipo: string) => tipo === 'application/pdf' || tipo.startsWith('image/');

export default function CampanhaCadastrar({ campanha, onClose, onSave, msgs }: { campanha: any, onClose: () => void, onSave: (campanha: any) => void, msgs: any }) {
  const { t } = useTranslation();
  const [descricao, setDescricao] = useState<string>(campanha?.descricao ?? '');
  const [quantidade, setQuantidade] = useState<number | null>(campanha?.quantidade ?? null);
  const [idPermissao, setIdPermissao] = useState<string | null>(null);
  const [permissoes, setPermissoes] = useState<any[]>([]);
  const [arquivo, setArquivo] = useState<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState<string | null>(campanha?.nomeArquivo ?? null);
  const [tipoArquivo, setTipoArquivo] = useState<string | null>(campanha?.tipoArquivo ?? null);
  const fileUploadRef = useRef<FileUpload>(null);

  useEffect(() => {
    getOpcoesConsulta().then((data) => {
      // O id criptografado muda a cada resposta, então o bind na edição é feito pela descrição.
      if (campanha?.id && campanha?.descricaoPermissao) {
        const permissao = data.find((p: { label: string }) => p.label === campanha.descricaoPermissao);
        if (permissao) setIdPermissao(permissao.id);
      }
      setPermissoes(data);
    }).catch(() => setPermissoes([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showErro = (detail: string) => {
    msgs.current?.clear();
    msgs.current?.show([{ sticky: false, life: 10000, severity: 'error', summary: 'Erro', detail, closable: false }]);
  };

  const onSelecionarArquivo = (event: { files: File[] }) => {
    const file = event.files?.[0];
    fileUploadRef.current?.clear();
    if (!file) return;
    if (!isTipoArquivoPermitido(file.type)) {
      showErro(t('arquivoTipoInvalido'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setArquivo(reader.result as string);
      setNomeArquivo(file.name);
      setTipoArquivo(file.type);
    };
    reader.readAsDataURL(file);
  };

  const removerArquivo = () => {
    setArquivo(null);
    setNomeArquivo(null);
    setTipoArquivo(null);
  };

  const save = () => {
    if (!descricao || !quantidade || quantidade <= 0 || !idPermissao) {
      showErro('Preencha a descrição, a quantidade e a consulta.');
      return;
    }
    onSave({
      id: campanha?.id,
      descricao,
      quantidade,
      idPermissao,
      arquivoBase64: arquivo,
      nomeArquivo,
      tipoArquivo,
    });
  };

  const isImagem = (tipoArquivo || '').startsWith('image/');

  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }}>
      <div className="flex flex-column gap-2">
        <div className="grid">
          <div className="col-12 md:col-6 p-fluid">
            <label htmlFor="descricao" className="font-bold block mb-2 required">{t('descricao')}</label>
            <InputText id="descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} className="w-full" />
          </div>
          <div className="col-12 md:col-6 p-fluid">
            <label htmlFor="permissao" className="font-bold block mb-2 required">{t('consulta')}</label>
            <Dropdown inputId="permissao" value={idPermissao} onChange={(e) => setIdPermissao(e.value)} options={permissoes} optionLabel="label" optionValue="id"
              placeholder={t('selecione') as string} filter className="w-full" />
          </div>
          <div className="col-12 md:col-6 p-fluid">
            <label htmlFor="quantidade" className="font-bold block mb-2 required">{t('quantidade')}</label>
            <InputNumber inputId="quantidade" value={quantidade} onValueChange={(e) => setQuantidade(e.value ?? null)} min={1} useGrouping={false} className="w-full" />
          </div>
          <div className="col-12 md:col-6 p-fluid">
            <label className="font-bold block mb-2">{t('arquivoPdfOuImagem')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <FileUpload ref={fileUploadRef} mode="basic" name="arquivo" accept="application/pdf,image/*" customUpload auto
                chooseLabel={t('selecionarArquivo') as string}
                chooseOptions={{ icon: 'pi pi-paperclip', style: { backgroundColor: '#183462', borderColor: '#183462' } }}
                uploadHandler={onSelecionarArquivo} />
              {nomeArquivo && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className={isImagem ? 'pi pi-image' : 'pi pi-file-pdf'} style={{ color: '#183462' }} />
                  <span>{nomeArquivo}</span>
                  <Button type="button" icon="pi pi-times" severity="danger" text onClick={removerArquivo} tooltip={t('remover') as string} aria-label="Remover" />
                </span>
              )}
            </div>
            {arquivo && isImagem && (
              <img alt={nomeArquivo ?? ''} src={arquivo} style={{ maxWidth: '100%', maxHeight: '160px', marginTop: '0.75rem', border: '1px solid #e0e0e0', borderRadius: '6px' }} />
            )}
          </div>
        </div>

        {/* Botões de ação */}
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", paddingTop: "1rem" }} className="flex">
          <Button label={t('fechar') as string} severity="secondary" outlined onClick={onClose} type="button" />
          <Button style={{ backgroundColor: '#183462', borderColor: '#183462' }} label={t('salvar') as string} type="submit" />
        </div>
      </div>
    </form>
  )
}
